import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import type { Permission } from "@/types/roles";
import { hasPermission } from "@/types/roles";

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Permission required to access this route */
  permission?: Permission;
  /** Redirect target when not authenticated (default: /auth/login) */
  loginRedirect?: string;
  /** Redirect target when authenticated but unauthorized */
  unauthorizedRedirect?: string;
}

export function ProtectedRoute({
  children,
  permission,
  loginRedirect = "/auth/login",
  // New owners lack studio permissions until onboarding creates the studio.
  unauthorizedRedirect = "/manage/onboarding",
}: ProtectedRouteProps) {
  const { user, permissions, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Only the auth session matters here. A missing profiles row must NOT
  // bounce the user to login (that was kicking Anja out of onboarding).
  if (!user) {
    return <Navigate to={loginRedirect} state={{ from: location }} replace />;
  }

  // Authenticated but missing required permission
  if (permission && !hasPermission(permissions, permission)) {
    return <Navigate to={unauthorizedRedirect} replace />;
  }

  return <>{children}</>;
}
